
import React, { useState, useEffect } from "react";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, CheckCircle, XCircle, Info, MessageSquareText, ExternalLink } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { type CurrentAffairsQuestion } from "@/types/current-affairs";
import MathRenderer from "@/components/mock-test/MathRenderer";

interface QuestionReviewCardProps {
  question: CurrentAffairsQuestion;
  userAnswerIndex: number | null;
  isCorrect: boolean;
  questionNumber: number;
}

const QuestionReviewCard: React.FC<QuestionReviewCardProps> = ({
  question,
  userAnswerIndex,
  isCorrect,
  questionNumber
}) => {
  const [showExplanation, setShowExplanation] = useState(false);
  const [explanation, setExplanation] = useState<string | null>(question.explanation || null);
  const [loadingExplanation, setLoadingExplanation] = useState(false);
  const [showSourceLink, setShowSourceLink] = useState(false);
  
  // If we don't have an explanation, generate one on first expansion
  const handleShowExplanation = async () => {
    setShowExplanation(!showExplanation);
    
    // If we already have an explanation, no need to generate one
    if (explanation || loadingExplanation) return;
    
    setLoadingExplanation(true);
    try {
      // Call the edge function to generate an explanation
      const { data, error } = await supabase.functions.invoke('generate-current-affairs-explanation', {
        body: {
          question: question.question,
          options: question.options,
          correctAnswerIndex: question.correctAnswer,
          userAnswerIndex,
          isCorrect,
          topic: question.topic || 'Current Affairs',
          difficulty: question.difficulty
        }
      });
      
      if (error) throw error;
      
      setExplanation(data.explanation);
    } catch (error) {
      console.error('Error generating explanation:', error);
      setExplanation('Failed to generate explanation. Please try again.');
    } finally {
      setLoadingExplanation(false);
    }
  };

  const toggleSourceLink = () => {
    setShowSourceLink(!showSourceLink);
  };
  
  return (
    <Card className={`overflow-hidden border ${isCorrect ? 'border-green-200' : 'border-red-200'}`}>
      <CardContent className="p-4">
        <div className="flex justify-between mb-2">
          <div className="flex items-center">
            <Badge className="mr-2 bg-primary/10 text-primary hover:bg-primary/20">
              Q{questionNumber}
            </Badge>
            <Badge variant="outline" className="capitalize">
              {question.difficulty}
            </Badge>
            {question.topic && (
              <Badge variant="secondary" className="ml-2">
                {question.topic}
              </Badge>
            )}
          </div>
          {isCorrect ? (
            <div className="flex items-center text-green-600 text-sm font-medium">
              <CheckCircle className="h-4 w-4 mr-1" />
              Correct
            </div>
          ) : (
            <div className="flex items-center text-red-600 text-sm font-medium">
              <XCircle className="h-4 w-4 mr-1" />
              Incorrect
            </div>
          )}
        </div>
        
        <h3 className="text-base font-medium mb-3">
          <MathRenderer text={question.question} />
        </h3>
        
        <div className="space-y-2">
          {question.options.map((option, idx) => {
            let optionClass = "border px-3 py-2 rounded-md text-sm";
            
            // User's answer
            if (idx === userAnswerIndex) {
              optionClass += isCorrect 
                ? " bg-green-50 border-green-300 text-green-800" 
                : " bg-red-50 border-red-300 text-red-800";
            }
            // Correct answer (show if user was wrong)
            else if (idx === question.correctAnswer && !isCorrect) {
              optionClass += " bg-green-50 border-green-300 text-green-800";
            }
            // Other options
            else {
              optionClass += " bg-gray-50 border-gray-200";
            }
            
            return (
              <div key={idx} className={optionClass}>
                <MathRenderer text={option} />
                {idx === question.correctAnswer && (
                  <span className="ml-2 text-xs text-green-600 font-medium">
                    (Correct answer)
                  </span>
                )}
              </div>
            );
          })}
        </div>
        
        {question.source && (
          <div className="mt-3">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={toggleSourceLink} 
              className="text-xs px-2 py-1 h-auto text-blue-600"
            >
              <ExternalLink className="h-3 w-3 mr-1" />
              {showSourceLink ? 'Hide source' : 'Show source'}
            </Button>
            {showSourceLink && (
              <a 
                href={question.source} 
                target="_blank" 
                rel="noopener noreferrer"
                className="block mt-1 text-xs text-blue-600 hover:underline truncate overflow-hidden"
              >
                {question.source}
              </a>
            )}
          </div>
        )}
      </CardContent>
      
      <CardFooter className="bg-gray-50 px-4 py-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleShowExplanation}
          className="text-xs w-full flex justify-center items-center"
        >
          {loadingExplanation ? (
            <>
              <Loader2 className="h-3 w-3 mr-2 animate-spin" />
              Generating explanation...
            </>
          ) : (
            <>
              {showExplanation ? (
                <Info className="h-3 w-3 mr-2" />
              ) : (
                <MessageSquareText className="h-3 w-3 mr-2" />
              )}
              {showExplanation ? "Hide Explanation" : "Show Explanation"}
            </>
          )}
        </Button>
      </CardFooter>
      
      {showExplanation && explanation && (
        <div className="px-4 py-3 bg-blue-50 text-sm border-t">
          <h4 className="font-medium mb-1 text-blue-700">Explanation:</h4>
          <div className="text-blue-800 whitespace-pre-line">
            <MathRenderer text={explanation} />
          </div>
        </div>
      )}
    </Card>
  );
};

export default QuestionReviewCard;
